import React, { useEffect } from 'react';
import { Routes, Route, useMatch, Navigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ClipPath from '../components/ClipPath';
import store from '../redux/store';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import {
  AssetPoc,
  ConnectSiemba,
  Home,
  PentestProfile,
  PentestReview,
  PentestScope,
  PentestTeam,
  Profile,
  UpdatePentest,
  SSOCallback,
  SignUp,
  VerifyCode,
} from './pages/PageListAsync';
import AdminRoutes from './Routes/AdminRoutes';
import AlertProvider from '../components/SuccessOrFailedModal';
import MyDashboardRoutes from './Routes/MyDashboardRoutes';
import TesterRoutes from './Routes/TesterRoutes';
import moment from 'moment';
import LayoutProvider from '../Providers/LayoutProvider';
import { AMPLITUDE_API_KEY, APP_MODE } from '../utils/constants';
import { AppModeProvider } from '../Providers/AppModeProvider';
import CollapsableAd from '../components/CollapsableAd';
import BogoOffer from '../components/Bogo';
import Bogo from '../components/Bogo';
import MobileExperienceToast from '../components/MobileExperienceToast';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

moment.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: '1s',
    ss: '%ds',
    m: '1m',
    mm: '%dm',
    h: '1h',
    hh: '%dh',
    d: '1d',
    dd: '%dd',
    M: '1mon',
    MM: '%dmon',
    y: '1y',
    yy: '%dy',
  },
});

function App() {
  const match = useMatch('admin/all_pentests/:pentestId/update');
  const customLayout = !!match;

  useEffect(() => {
    const amplitudeAPIKey = AMPLITUDE_API_KEY;
    if (!amplitudeAPIKey) {
      console.warn('Amplitude API key is not set. Skipping Amplitude initialization.');
      return;
    }
  
    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = resolve;
        s.onerror = reject;
        document.body.appendChild(s);
      });
    };
  
    const initAmplitude = async () => {
      try {
        await Promise.all([
          loadScript('https://cdn.amplitude.com/libs/analytics-browser-2.11.1-min.js.gz'),
          loadScript('https://cdn.amplitude.com/libs/plugin-session-replay-browser-1.8.0-min.js.gz'),
          loadScript(`https://cdn.amplitude.com/script/${amplitudeAPIKey}.engagement.js`)
        ]);
  
        if (window.amplitude) {
          if (window.sessionReplay) {
            window.amplitude.add(window.sessionReplay.plugin({ sampleRate: 1 }));
          }
          if (window.engagement) {
            window.amplitude.add(window.engagement.plugin());
          }
  
          window.amplitude.init(amplitudeAPIKey, {
            autocapture: { elementInteractions: true },
          });
        }
      } catch (error) {
        console.error('Failed to load Amplitude scripts:', error);
      }
    };
  
    initAmplitude();
  }, []);
  

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <AppModeProvider>
          <AlertProvider>
            <MobileExperienceToast/>
            {customLayout ? (
              <Routes>
                <Route
                  path="/admin/all_pentests/:pentest_id/update"
                  element={
                    <LayoutProvider
                      value={{
                        dashboard: 'ADMIN_DASHBOARD',
                        mainUrl: '/admin',
                      }}
                    >
                      <UpdatePentest />
                    </LayoutProvider>
                  }
                />
              </Routes>
            ) : (
              <>
                <div className="min-h-screen flex flex-col pb-4 md:pb-0">
                  {/* <CollapsableAd endDateTime="31/08/2025" /> */}
                  <Header />
                  <ClipPath />
                  <Routes>
                    <Route path="/" element={<Home />} />
                    {APP_MODE === 'genptbeta' && <Route path="/cpanel" element={<Home />} />}
                    <Route path="/signup" element={<Navigate to="/" replace />} />
                    <Route path="/verify_code" element={<VerifyCode />} />
                    <Route path="/:uuid/sso-callback" element={<SSOCallback />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/connect_siemba" element={<ConnectSiemba />} />
                    <Route path="/pentest">
                      <Route path="profile" element={<PentestProfile />} />
                      <Route path="scope" element={<PentestScope />} />
                      <Route path="team" element={<PentestTeam />} />
                      <Route path="team_overseers" element={<PentestTeam />} />
                      <Route path="team_asset_poc" element={<AssetPoc />} />
                      <Route path="review" element={<PentestReview />} />
                    </Route>
                    <Route path="/admin/*" element={<AdminRoutes />} />
                    <Route path="/app/*" element={<MyDashboardRoutes />} />
                    <Route path="/tester/*" element={<TesterRoutes />} />
                  </Routes>
                  <Footer />
                </div>
              </>
            )}
             {/* {APP_MODE === 'genptbeta' &&  <Bogo />} */}
           
          </AlertProvider>
        </AppModeProvider>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;
